import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Search, Cpu, Settings2 } from "lucide-react";
import { ECM_DB, lookupEcm } from "../flashtools/lib/ecmDatabase";

// ECM DATABASE is now linked to the admin ECU Database (collection `ecu_database`, /ecu-database) —
// the SAME records Flash Tools resolves from. Grouped by ECU maker: Shindengen / Keihin / Denso.
const MAKERS = ["Shindengen", "Keihin", "Denso"];
const TAB_ACTIVE = {
  All: "bg-slate-200 text-slate-950 border-slate-200",
  Shindengen: "bg-slate-200 text-slate-950 border-slate-200",
  Keihin: "bg-slate-200 text-slate-950 border-slate-200",
  Denso: "bg-slate-200 text-slate-950 border-slate-200",
};
const MAKER_BORDER = {
  Shindengen: "border-l-slate-600",
  Keihin: "border-l-red-600",
  Denso: "border-l-slate-400",
};

const COLS = [
  ["part_number", "Part Number"], ["ecm_id", "ECM / Chip ID"], ["ecu_type", "ECU Type"],
  ["mcu_type", "MCU Type"], ["model", "Model"], ["year", "Year"],
  ["flash_size", "Flash (KB)"], ["start_offset", "Start"], ["checksum_offset", "Checksum"],
];

export default function DatabasePage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [backendRows, setBackendRows] = useState([]);
  const [q, setQ] = useState(() => { try { return new URLSearchParams(window.location.search).get("part") || ""; } catch { return ""; } });
  const [maker, setMaker] = useState(() => { try { return new URLSearchParams(window.location.search).get("type") || "All"; } catch { return "All"; } });

  const load = async () => {
    try { setBackendRows((await api.get("/ecu-database")).data || []); } catch { setBackendRows([]); }
  };
  useEffect(() => { load(); }, []);

  // Built-in Flash Tools ECU catalog (ECM_DB) mapped to the table row shape — shown automatically so
  // the database contains everything Flash Tools can resolve, without any destructive writes.
  const builtinRows = useMemo(() => Object.keys(ECM_DB).map((id) => {
    const e = ECM_DB[id];
    const info = lookupEcm(id);
    return {
      id: `builtin-${id}`,
      part_number: e.part || info.part || "Unknown",
      ecm_id: id,
      ecu_type: e.maker || info.maker || "Other",
      mcu_type: e.mcu || info.mcu || "",
      model: e.model || e.name || "",
      year: "",
      flash_size: e.flash || "",
      start_offset: (e.start ?? "") === "" ? "Auto" : e.start,
      checksum_offset: (e.checksum ?? "") === "" ? "Auto" : e.checksum,
    };
  }), []);

  // Merge: built-in catalog + admin records; an admin record (same ECM ID) overrides the built-in one.
  const rows = useMemo(() => {
    const norm = (s) => (s || "").toUpperCase().replace(/[^0-9A-F]/g, "");
    const map = {};
    builtinRows.forEach((r) => { map[norm(r.ecm_id)] = r; });
    backendRows.forEach((r) => { const k = norm(r.ecm_id); if (k) map[k] = r; });
    return Object.values(map).sort((a, b) =>
      (a.ecu_type || "").localeCompare(b.ecu_type || "") || (a.part_number || "").localeCompare(b.part_number || ""));
  }, [builtinRows, backendRows]);

  // Tabs: the 3 requested makers first, then any other ECU types present in the data.
  const makerTabs = useMemo(() => {
    const extra = [...new Set(rows.map((r) => r.ecu_type).filter(Boolean))].filter((t) => !MAKERS.includes(t));
    return ["All", ...MAKERS, ...extra];
  }, [rows]);

  const makerCount = useMemo(() => {
    const c = {};
    rows.forEach((r) => { c[r.ecu_type] = (c[r.ecu_type] || 0) + 1; });
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      const matchMaker = maker === "All" || (r.ecu_type || "") === maker;
      const s = q.toLowerCase();
      const matchQ = !s || Object.values(r).some((v) => String(v).toLowerCase().includes(s));
      return matchMaker && matchQ;
    });
  }, [rows, q, maker]);

  const cell = (key, r) => {
    if (key === "flash_size") return r.flash_size ? `${r.flash_size} KB` : "—";
    return r[key] || "—";
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1400px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400/90 flex items-center gap-2"><Cpu className="h-4 w-4" /> Page 01</div>
          <h1 className="font-head font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">ECM Database</h1>
          <p className="text-slate-500 text-sm mt-1">Full Flash Tools ECU catalog by maker — Shindengen · Keihin · Denso. Auto-resolves in Flash Tools.</p>
        </div>
        {isAdmin && (
          <Button data-testid="ecu-db-manage-button" onClick={() => navigate("/admin/ecu-database")}
            className="bg-gradient-to-r from-cyan-400 to-teal-500 hover:from-cyan-300 hover:to-teal-400 text-slate-950 font-bold">
            <Settings2 className="h-4 w-4 mr-1.5" /> Manage ECUs
          </Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input data-testid="ecu-db-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search part, ECM ID, MCU, type…"
            className="pl-9 bg-[#0c1017] border-slate-700 text-slate-100 focus-visible:ring-cyan-400 font-mono-tt" />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {makerTabs.map((b) => (
            <button key={b} data-testid={`ecu-db-maker-filter-${b}`} onClick={() => setMaker(b)}
              className={`px-3 py-2 rounded-md text-xs font-semibold uppercase tracking-wider border transition-colors ${
                maker === b ? (TAB_ACTIVE[b] || "bg-slate-200 text-slate-950 border-slate-200") : "bg-[#0c1017] text-slate-400 border-slate-700 hover:border-slate-500"}`}>
              {b}{b !== "All" && makerCount[b] ? ` ${makerCount[b]}` : ""}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-800 overflow-hidden bg-[#0c1017]">
        <div className="overflow-auto max-h-[70vh]">
          <table data-testid="ecu-db-table" className="w-full text-sm">
            <thead>
              <tr className="bg-[#121824] border-b border-slate-800">
                {COLS.map(([, label]) => (
                  <th key={label} className="sticky top-0 bg-[#121824] text-left px-3 py-3 font-mono-tt text-[11px] uppercase tracking-wider text-cyan-400/90 whitespace-nowrap">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.id} data-testid={`ecu-db-row-${r.id}`} className={`border-b border-slate-800/60 border-l-4 ${MAKER_BORDER[r.ecu_type] || "border-l-slate-700"} ${i % 2 ? "bg-slate-950/40" : "bg-transparent"} hover:bg-cyan-400/5`}>
                  {COLS.map(([key]) => (
                    <td key={key} className={`px-3 py-2.5 whitespace-nowrap ${
                      key === "part_number" ? "font-semibold text-white"
                      : key === "ecm_id" ? "font-mono-tt text-slate-300"
                      : "font-mono-tt text-slate-300"}`}>{cell(key, r)}</td>
                  ))}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={COLS.length} className="px-3 py-10 text-center text-slate-600 font-mono-tt">No records match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="text-xs font-mono-tt text-slate-600 mt-3">{filtered.length} of {rows.length} records</div>
    </div>
  );
}
